import { Injectable, inject } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Share } from "@capacitor/share";
import { AlertController, LoadingController, ModalController, Platform, ToastController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { NgxImageCompressService } from "ngx-image-compress";
import { firstValueFrom, skipWhile, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { IEvent, IEventUser, IFullCustomField, IGroup, ILocation, ITrack } from "../interfaces";
import { PathComponents } from "../paths/path-components";
import { FirestoreService } from "./firestore.service";
import { Network } from "@capacitor/network";
import { Store } from "@ngrx/store";
import { NetworkStatus } from "../actions/utility.actions";
import { IFilters } from "../interfaces/filters.interfaces";
import { TypeCustomFields } from "../enums/type-custom-fields";
import { v4 as uuidv4 } from "uuid";
import { WritableSignal, signal } from "@angular/core";
import { selectUrl } from "../selectors/router.selectors";
import { getCurrentContainer } from "../selectors/containers.selectors";
import { getCurrentEvent } from "../selectors/events.selectors";
import { DOCUMENT } from "@angular/common";

@Injectable({
	providedIn: "root"
})
export class UtilityService {
	document = inject(DOCUMENT);
	platform = inject(Platform);
	goBackSubject: Subject<boolean> = new Subject();
	headerMenuVisibility: Subject<boolean> = new Subject();

	showAlertModal: WritableSignal<boolean> = signal(false);
	alertModalTitle: WritableSignal<string> = signal("");
	alertModalMessage: WritableSignal<string> = signal("");

	currentFavicon: WritableSignal<string> = signal("");

	constructor(
		private toastCtrl: ToastController,
		private STranslate: TranslateService,
		private alertCtrl: AlertController,
		private loadingCtrl: LoadingController,
		private modalCtrl: ModalController,
		private snackbar: MatSnackBar,
		private imageCompress: NgxImageCompressService,
		private SFirestore: FirestoreService,
		private store: Store
	) {}

	getAppSettings() {
		return this.SFirestore.valueChangesDocument("app-settings/settings");
	}

	/**
	 * Manage favicon based on url and container or event
	 */
	manageFavicon() {
		this.store.select(selectUrl).subscribe(async (url) => {
			if (url) {
				const currentContainer = await firstValueFrom(
					this.store.select(getCurrentContainer).pipe(skipWhile((container) => !container))
				);

				if (url.includes("event") && !url.includes("events")) {
					const currentEvent = await firstValueFrom(
						this.store.select(getCurrentEvent).pipe(skipWhile((event) => !event))
					);
					if (currentEvent && this.currentFavicon() !== currentEvent?.identity.touchIcon) {
						this.currentFavicon.set(currentEvent?.identity.touchIcon);
						this.document
							.getElementById("appFavicon")
							.setAttribute("href", currentEvent?.identity.touchIcon);
						this.document
							.getElementById("appAppleFavicon")
							.setAttribute("href", currentEvent?.identity.touchIcon);
						this.document
							.getElementById("appAppleStartupIcon")
							.setAttribute("href", currentEvent?.identity.touchIcon);
					}
				} else if (currentContainer && this.currentFavicon() !== currentContainer?.favicon) {
					this.currentFavicon.set(currentContainer?.favicon);
					this.document.getElementById("appFavicon").setAttribute("href", currentContainer?.favicon);
					this.document.getElementById("appAppleFavicon").setAttribute("href", currentContainer?.favicon);
					this.document.getElementById("appAppleStartupIcon").setAttribute("href", currentContainer?.favicon);
				}
			}
		});
	}

	listenToNetworkStatus() {
		Network.getStatus().then((status) => {
			this.store.dispatch(NetworkStatus({ payload: status.connected }));
		});
		// Network status & event user presence status
		Network.addListener("networkStatusChange", (status) => {
			this.store.dispatch(NetworkStatus({ payload: status.connected }));
		});
	}

	/**
	 * Present a toast
	 * @param message
	 * @param duration
	 * @param position
	 * @param closeButton
	 * @param color
	 */
	async presentToast(message: string, duration: number, position: "top" | "bottom" | "middle", color?: string) {
		const toast = await this.toastCtrl.create({
			message: message,
			duration: duration,
			position: position,
			positionAnchor: "toastBottomPos",
			color: color ? color : "",
			cssClass: "toast-message",
			animated: true,
			swipeGesture: "vertical"
		});

		toast.present();
	}

	/**
	 * Present an alert
	 * @param title
	 * @param message
	 * @param buttons
	 * @param inputs
	 */
	async presentAlert(title: string, message: string, buttons = [], inputs = [], backdropDismiss?: boolean) {
		const alert = await this.alertCtrl.create({
			header: title,
			message: message,
			buttons: buttons,
			inputs: inputs,
			backdropDismiss: backdropDismiss ? backdropDismiss : true
		});

		return alert;
	}

	/**
	 * Present loading
	 * @param message
	 * @param duration
	 * @param spinner
	 */
	async presentLoading(
		message: string,
		duration: number,
		spinner: "bubbles" | "circles" | "circular" | "crescent" | "dots" | "lines" | "lines-small"
	) {
		const loader = await this.loadingCtrl.create({
			message: message,
			duration: duration,
			spinner: spinner,
			cssClass: "spinner-css-class"
		});

		return loader;
	}

	/**
	 * Convert language
	 * @param language
	 */
	convertLanguage(language: string) {
		switch (language) {
			case "ar_AR":
				return "ArAR";
			case "de_DE":
				return "DeDE";
			case "en_US":
				return "EnUS";
			case "es_ES":
				return "EsES";
			case "fr_FR":
				return "FrFR";
			case "pt_BR":
				return "PtBR";
			default:
				return environment.platform.defaultLanguage;
		}
	}

	/**
	 * Get locale
	 * @param language
	 * @returns
	 */
	getLocale(language: string) {
		return language.slice(0, 2).toLowerCase() + "-" + language.slice(2, 4).toUpperCase();
	}

	getDateWithTimezoneType(event: IEvent, type: string, date: string) {
		return !type || type === "event" ? DateTime.fromISO(date).setZone(event.timezone) : DateTime.fromISO(date);
	}

	configureDateTime(event: IEvent, eventUser: IEventUser, date: DateTime) {
		date = date.setLocale(
			this.STranslate.currentLang
				? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
						"-" +
						this.STranslate.currentLang.slice(2, 4).toUpperCase()
				: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
		);

		if (
			event &&
			event.timezoneType === "local" &&
			eventUser &&
			eventUser.updatedSettings &&
			eventUser.updatedSettings.timezoneType === "event"
		) {
			date = date.setZone(event.timezone);
		}
		return date;
	}

	getDateOnConfigTimezone(event: IEvent, eventUser: IEventUser, date: string) {
		let newDate: DateTime;
		if (
			(event.timezoneType === "local" &&
				(!eventUser || (eventUser && !eventUser.updatedSettings.timezoneType))) ||
			(event.timezoneType === "local" && eventUser && eventUser.updatedSettings.timezoneType === "local")
		) {
			newDate = DateTime.fromISO(date).setLocale(
				this.STranslate.currentLang
					? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
							"-" +
							this.STranslate.currentLang.slice(2, 4).toUpperCase()
					: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
			);
		} else if (event.timezoneType === "local" && eventUser && eventUser.updatedSettings.timezoneType === "event") {
			newDate = DateTime.fromISO(date)
				.setLocale(
					this.STranslate.currentLang
						? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
								"-" +
								this.STranslate.currentLang.slice(2, 4).toUpperCase()
						: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
				)
				.setZone(event.timezone);
		} else if (event.timezoneType === "date") {
			newDate = DateTime.fromISO(date && date.split("+").length > 0 ? date.split("+")[0] : date).setLocale(
				this.STranslate.currentLang
					? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
							"-" +
							this.STranslate.currentLang.slice(2, 4).toUpperCase()
					: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
			);
		}
		return newDate;
	}

	formatDate(event: IEvent, eventUser: IEventUser, date: string, unit: string, locale?: string) {
		return this.getDateWithTimezoneType(
			event,
			eventUser && eventUser.updatedSettings ? eventUser.updatedSettings.timezoneType : event.timezoneType,
			date
		)
			.setLocale(
				locale
					? locale
					: this.STranslate.currentLang
						? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
							"-" +
							this.STranslate.currentLang.slice(2, 4).toUpperCase()
						: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
			)
			.toFormat(
				unit === "short" &&
					(this.STranslate.currentLang === "DeDE" ||
						this.STranslate.currentLang === "FrFR" ||
						this.STranslate.currentLang === "PtBR" ||
						this.STranslate.currentLang === "EsES" ||
						this.STranslate.currentLang === "ArAR")
					? "dd/MM/yyyy"
					: unit === "short" && this.STranslate.currentLang === "EnUS"
						? "MM/dd/yyyy"
						: unit === "full"
							? "EEEE, d MMMM y"
							: unit === "day"
								? "dd"
								: unit === "month"
									? "MMM"
									: unit === "long"
										? "dd MMMM yyyy"
										: unit === "time" &&
											  (this.STranslate.currentLang === "DeDE" ||
													this.STranslate.currentLang === "FrFR" ||
													this.STranslate.currentLang === "PtBR" ||
													this.STranslate.currentLang === "EsES" ||
													this.STranslate.currentLang === "ArAR")
											? "HH:mm"
											: "hh:mm a"
			);
	}

	/**
	 * Get part of date
	 * @param date
	 * @param unit
	 * @returns
	 */
	getPartOfDate(event: IEvent, eventUser: IEventUser, date: string, unit: string) {
		let dateString = "";
		if (
			(event.timezoneType === "local" &&
				(!eventUser || (eventUser && !eventUser.updatedSettings.timezoneType))) ||
			(event.timezoneType === "local" && eventUser && eventUser.updatedSettings.timezoneType === "local")
		) {
			dateString = DateTime.fromISO(date)
				.setLocale(
					this.STranslate.currentLang
						? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
								"-" +
								this.STranslate.currentLang.slice(2, 4).toUpperCase()
						: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
				)
				.toFormat(
					unit === "short" &&
						(this.STranslate.currentLang === "DeDE" ||
							this.STranslate.currentLang === "FrFR" ||
							this.STranslate.currentLang === "PtBR" ||
							this.STranslate.currentLang === "EsES" ||
							this.STranslate.currentLang === "ArAR")
						? "dd/MM/yyyy"
						: unit === "short" && this.STranslate.currentLang === "EnUS"
							? "MM/dd/yyyy"
							: unit === "full"
								? "EEEE, d MMMM y"
								: unit === "day"
									? "dd"
									: unit === "month"
										? "MMM"
										: unit === "long"
											? "dd MMMM yyyy"
											: unit === "time" &&
												  (this.STranslate.currentLang === "DeDE" ||
														this.STranslate.currentLang === "FrFR" ||
														this.STranslate.currentLang === "PtBR" ||
														this.STranslate.currentLang === "EsES" ||
														this.STranslate.currentLang === "ArAR")
												? "HH:mm"
												: "hh:mm a"
				);
		} else if (event.timezoneType === "local" && eventUser && eventUser.updatedSettings.timezoneType === "event") {
			dateString = DateTime.fromISO(date)
				.setLocale(
					this.STranslate.currentLang
						? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
								"-" +
								this.STranslate.currentLang.slice(2, 4).toUpperCase()
						: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
				)
				.setZone(event.timezone)
				.toFormat(
					unit === "short" &&
						(this.STranslate.currentLang === "DeDE" ||
							this.STranslate.currentLang === "FrFR" ||
							this.STranslate.currentLang === "PtBR" ||
							this.STranslate.currentLang === "EsES" ||
							this.STranslate.currentLang === "ArAR")
						? "dd/MM/yyyy"
						: unit === "short" && this.STranslate.currentLang === "EnUS"
							? "MM/dd/yyyy"
							: unit === "full"
								? "EEEE, d MMMM y"
								: unit === "day"
									? "dd"
									: unit === "month"
										? "MMM"
										: unit === "long"
											? "dd MMMM yyyy"
											: unit === "time" &&
												  (this.STranslate.currentLang === "DeDE" ||
														this.STranslate.currentLang === "FrFR" ||
														this.STranslate.currentLang === "PtBR" ||
														this.STranslate.currentLang === "EsES" ||
														this.STranslate.currentLang === "ArAR")
												? "HH:mm"
												: "hh:mm a"
				);
		} else if (event.timezoneType === "date") {
			dateString = DateTime.fromISO(date ? date.slice(0, 23) : date)
				.setLocale(
					this.STranslate.currentLang
						? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
								"-" +
								this.STranslate.currentLang.slice(2, 4).toUpperCase()
						: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
				)
				.toFormat(
					unit === "short" &&
						(this.STranslate.currentLang === "DeDE" ||
							this.STranslate.currentLang === "FrFR" ||
							this.STranslate.currentLang === "PtBR" ||
							this.STranslate.currentLang === "EsES" ||
							this.STranslate.currentLang === "ArAR")
						? "dd/MM/yyyy"
						: unit === "short" && this.STranslate.currentLang === "EnUS"
							? "MM/dd/yyyy"
							: unit === "full"
								? "EEEE, d MMMM y"
								: unit === "day"
									? "dd"
									: unit === "month"
										? "MMM"
										: unit === "long"
											? "dd MMMM yyyy"
											: unit === "time" &&
												  (this.STranslate.currentLang === "DeDE" ||
														this.STranslate.currentLang === "FrFR" ||
														this.STranslate.currentLang === "PtBR" ||
														this.STranslate.currentLang === "EsES" ||
														this.STranslate.currentLang === "ArAR")
												? "HH:mm"
												: "hh:mm a"
				);
		} else {
			dateString = this.STranslate.instant("schedule_detail.no_date");
		}

		return dateString;
	}

	/**
	 * Get part of date
	 * @param date
	 * @param unit
	 * @returns
	 */
	getPartOfDateWitoutLocale(date: string, unit: string) {
		let dateString = "";

		dateString = DateTime.fromISO(date?.split("T")?.[0] || date)
			.setLocale(this.STranslate.currentLang.slice(0, 2).toLowerCase())
			.toFormat(
				unit === "short" &&
					(this.STranslate.currentLang === "DeDE" ||
						this.STranslate.currentLang === "FrFR" ||
						this.STranslate.currentLang === "PtBR" ||
						this.STranslate.currentLang === "EsES" ||
						this.STranslate.currentLang === "ArAR")
					? "dd/MM/yyyy"
					: unit === "short" && this.STranslate.currentLang === "EnUS"
						? "MM/dd/yyyy"
						: unit === "full"
							? "EEEE, d MMMM y"
							: unit === "day"
								? "dd"
								: unit === "month"
									? "MMM"
									: "dd MMMM yyyy"
			);

		return dateString;
	}

	/**
	 * getDateFormatBasedOnEvent
	 * @param date
	 * @param language
	 * @returns
	 */
	setLocaleAndZoneOfDatetime(event: IEvent, eventUser: IEventUser, date: DateTime) {
		if (
			(event.timezoneType === "local" &&
				(!eventUser || (eventUser && !eventUser.updatedSettings.timezoneType))) ||
			(event.timezoneType === "local" && eventUser && eventUser.updatedSettings.timezoneType === "local")
		) {
			date = date.setLocale(
				this.STranslate.currentLang
					? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
							"-" +
							this.STranslate.currentLang.slice(2, 4).toUpperCase()
					: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
			);
		} else if (event.timezoneType === "local" && eventUser && eventUser.updatedSettings.timezoneType === "event") {
			date = date
				.setLocale(
					this.STranslate.currentLang
						? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
								"-" +
								this.STranslate.currentLang.slice(2, 4).toUpperCase()
						: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
				)
				.setZone(event.timezone);
		} else if (event.timezoneType === "date") {
			date = date.setLocale(
				this.STranslate.currentLang
					? this.STranslate.currentLang.slice(0, 2).toLowerCase() +
							"-" +
							this.STranslate.currentLang.slice(2, 4).toUpperCase()
					: event.language.slice(0, 2).toLowerCase() + "-" + event.language.slice(2, 4).toUpperCase()
			);
		}
		return date;
	}

	/**
	 * getDateFormat
	 * @param date
	 * @param language
	 * @returns
	 */
	getDateFormat(date: string, language: string) {
		if (!date) return "";
		const format =
			language === "DeDE" ||
			language === "FrFR" ||
			language === "PtBR" ||
			language === "EsES" ||
			language === "ArAR"
				? "dd/MM/yyyy"
				: "MM/dd/yyyy";

		return DateTime.fromISO(date).toFormat(format);
	}

	/**
	 * alertLoginRegister
	 * @param alertTitle
	 * @param message
	 */
	async alertLoginRegister(eventId: string, alertTitle: string, message?: string, isLoggedIn?: boolean) {
		const alert = await this.alertCtrl.create({
			header: alertTitle,
			message: message ? message : this.STranslate.instant("texts.need_login_to_use"),
			buttons: !isLoggedIn
				? [
						{
							text: this.STranslate.instant("buttons.authenticate"),
							handler: async () => this.openLogin(eventId)
						},
						{
							text: this.STranslate.instant("buttons.cancel"),
							role: "cancel",
							handler: () => {
								//
							}
						}
					]
				: [
						{
							text: this.STranslate.instant("buttons.ok"),
							role: "cancel",
							handler: () => {
								//
							}
						}
					],
			backdropDismiss: true,
			cssClass: "center-alert-items"
		});

		alert.present();
	}

	/**
	 * Open login modal
	 * @returns
	 */
	async openLogin(eventId: string) {
		try {
			const modal = await this.modalCtrl.create({
				component: PathComponents.loginModal,
				componentProps: {
					eventId: eventId
				},
				cssClass: "full-sized-modal"
			});
			await modal.present();

			const { data } = await modal.onWillDismiss();
			if (data && data.openRegister) {
				this.openRegister(eventId);
			}
		} catch (error) {
			this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * Open register modal
	 * @returns
	 */
	async openRegister(eventId: string) {
		try {
			const modal = await this.modalCtrl.create({
				component: PathComponents.registerForm,
				componentProps: {
					eventId: eventId
				},
				cssClass: "full-sized-modal"
			});
			await modal.present();

			const { data } = await modal.onWillDismiss();
			if (data && data.openLogin) {
				this.openLogin(eventId);
			}
		} catch (error) {
			this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	removeAccents(word: string): string {
		let copyWord: string = word.slice();
		const map = [
			["[àáâãäå]", "a"],
			["æ", "ae"],
			["ç", "c"],
			["[èéêë]", "e"],
			["[ìíîï]", "i"],
			["ñ", "n"],
			["[òóôõö]", "o"],
			["œ", "oe"],
			["[ùúûü]", "u"],
			["[ýÿ]", "y"]
		];

		for (let i = 0; i < map.length; ++i) {
			copyWord = copyWord.replace(new RegExp(map[i][0], "gi"), function (match) {
				if (match.toLowerCase() === match) {
					return map[i][1].toLowerCase();
				} else {
					return map[i][1];
				}
			});
		}

		return copyWord;
	}

	shareImage(url: any) {
		// let opt: ShareOptions;
		Share.share({
			url: url
		});
	}

	shareText(title: string, text: string, dialogTitle: string) {
		return Share.share({
			title: title,
			text: text,
			dialogTitle: dialogTitle
		});
	}

	/**
	 * compressImage
	 * @param image
	 * @param ratio
	 * @param quality
	 * @returns
	 */
	compressImage(image: string, ratio: number, quality: number): Promise<string> {
		return this.imageCompress.compressFile(image, null, ratio, quality);
	}

	/**
	 * getDatePipeData
	 * @param eventLanguage
	 * @param formatType
	 * @returns
	 */
	getDatePipeData(eventLanguage: string, formatType: string): string[] {
		if (!eventLanguage || !formatType) return ["dd/MM/yyyy", "", ""];
		return [
			eventLanguage === "DeDE" ||
			eventLanguage === "FrFR" ||
			eventLanguage === "PtBR" ||
			eventLanguage === "EsES" ||
			eventLanguage === "ArAR"
				? formatType === "short"
					? "dd/MM/yyyy"
					: "EEEE, d MMMM y"
				: formatType === "short"
					? "MM/dd/yyyy"
					: "EEEE, MMMM d y",
			"",
			eventLanguage
				.replace(/(\w{2})(\w{2})/, "$1")
				.toLowerCase()
				.concat(`-${eventLanguage.replace(/(\w{2})(\w{2})/, "$2")}`)
		];
	}

	/**
	 * getDatePipeData
	 * @param eventLanguage
	 * @param formatType
	 * @returns
	 */
	getDateTimePipeData(eventLanguage: string): string[] {
		if (!eventLanguage) return ["HH:mm", "", "fr-FR"];
		return [
			eventLanguage === "DeDE" ||
			eventLanguage === "FrFR" ||
			eventLanguage === "PtBR" ||
			eventLanguage === "EsES" ||
			eventLanguage === "ArAR"
				? "HH:mm"
				: "hh:mm a",
			"",
			eventLanguage
				.replace(/(\w{2})(\w{2})/, "$1")
				.toLowerCase()
				.concat(`-${eventLanguage.replace(/(\w{2})(\w{2})/, "$2")}`)
		];
	}

	// replace a char at a specific index by its lowercase
	replaceCharAt(str: string, index: number, replacement: string) {
		return str.substr(0, index) + replacement + str.substr(index + replacement.length);
	}

	callGoBackOnHeader() {
		this.goBackSubject.next(true);
	}

	hideMenuAndHeader() {
		this.headerMenuVisibility.next(false);
	}

	showMenuAndHeader() {
		this.headerMenuVisibility.next(true);
	}

	computeReachableDatas(
		event: IEvent,
		allDatas: any[],
		filteredDatas: any[],
		computedCustomFields: IFullCustomField[],
		filters: IFilters,
		currentLanguage: string,
		groups: IGroup[],
		locations: ILocation[],
		tracks: ITrack[],
		datasGetType: "firestore" | "mongo"
	) {
		const notSelectedDatas: IFilters = {
			locations: [],
			tracks: [],
			customFields: [],
			groups: [],
			principalKey: ""
		};
		notSelectedDatas.locations = filters.locations.filter(() =>
			filters.locations.every((location) => !location.checked)
		);
		notSelectedDatas.groups = filters.groups.filter(() => filters.groups.every((group) => !group.checked));
		notSelectedDatas.tracks = filters.tracks.filter(() => filters.tracks.every((track) => !track.checked));
		notSelectedDatas.customFields = filters.customFields.filter(
			(filter) => !filter.values.find((value) => value.isSelected)
		);

		filters.locations =
			datasGetType === "mongo"
				? locations.map((loc) => {
						const filterLoc = filters.locations.find((filter) => filter.uid === loc.uid);
						return {
							uid: loc.uid,
							name: loc.name,
							checked: filterLoc ? filterLoc.checked : false,
							isReachable: filterLoc ? filterLoc.isReachable : true,
							totalDatas: 1
						};
					})
				: filters.locations.map((filter) => {
						const total =
							filters.principalKey === "locations"
								? allDatas.filter((data) => {
										if (data["locations"]) {
											if (data["locations"].includes(filter.uid)) {
												return true;
											} else {
												return false;
											}
										} else {
											return false;
										}
									}).length
								: filteredDatas.filter((data) => {
										if (data["locations"]) {
											if (data["locations"].includes(filter.uid)) {
												return true;
											} else {
												return false;
											}
										} else {
											return false;
										}
									}).length;

						filter.totalDatas = datasGetType === "firestore" ? total : 1;
						return filter;
					});

		filters.groups =
			datasGetType === "mongo"
				? groups.map((group) => {
						const filterGroup = filters.groups.find((filter) => filter.uid === group.uid);
						return {
							uid: group.uid,
							name: group.name,
							checked: filterGroup ? filterGroup.checked : false,
							isReachable: filterGroup ? filterGroup.isReachable : true,
							totalDatas: 1
						};
					})
				: filters.groups.map((filter) => {
						const total =
							filters.principalKey === "groups"
								? allDatas.filter((data) => {
										if (data["groups"]) {
											if (data["groups"].includes(filter.uid)) {
												return true;
											} else {
												return false;
											}
										} else {
											return false;
										}
									}).length
								: filteredDatas.filter((data) => {
										if (data["groups"]) {
											if (data["groups"].includes(filter.uid)) {
												return true;
											} else {
												return false;
											}
										} else {
											return false;
										}
									}).length;

						filter.totalDatas = datasGetType === "firestore" ? total : 1;
						return filter;
					});

		filters.tracks =
			datasGetType === "mongo"
				? tracks.map((track) => {
						const filterTrack = filters.tracks.find((filter) => filter.uid === track.uid);
						return {
							uid: track.uid,
							name: track.name,
							checked: filterTrack ? filterTrack.checked : false,
							isReachable: filterTrack ? filterTrack.isReachable : true,
							totalDatas: 1
						};
					})
				: filters.tracks.map((filter) => {
						const total =
							filters.principalKey === "tracks"
								? allDatas.filter((data) => {
										if (data["tracks"]) {
											if (data["tracks"].includes(filter.uid)) {
												return true;
											} else {
												return false;
											}
										} else {
											return false;
										}
									}).length
								: filteredDatas.filter((data) => {
										if (data["tracks"]) {
											if (data["tracks"].includes(filter.uid)) {
												return true;
											} else {
												return false;
											}
										} else {
											return false;
										}
									}).length;

						filter.totalDatas = datasGetType === "firestore" ? total : 1;
						return filter;
					});

		filters.customFields = filters.customFields.map((filter) => {
			const customField = computedCustomFields.find((computed) => computed.baseSettings?.uid === filter?.uid);

			if (customField) {
				filter.values = filter.values.map((value) => {
					const principalKeySplit = filters.principalKey.split("|");

					const total =
						principalKeySplit[0] === "customFields" && principalKeySplit[1] === value.filterId
							? allDatas.filter((data) => {
									const custom = data.customFields.find((cus) => {
										return (
											cus &&
											cus.uid === filter.uid &&
											(((this.getCustomFieldType(computedCustomFields, cus.uid) ===
												TypeCustomFields.TEXT ||
												this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.SELECT) &&
												value.value.toString().toLowerCase() ===
													cus.field.multiLanguageText[currentLanguage]
														.toString()
														.toLowerCase()) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field.multiLanguageText &&
													cus.field.multiLanguageText[currentLanguage] &&
													cus.field.multiLanguageText[currentLanguage] === "") ||
												((this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.URL ||
													this.getCustomFieldType(computedCustomFields, cus.uid) ===
														TypeCustomFields.EMAIL) &&
													value.value.toString().toLowerCase() ===
														cus.field.text.toLowerCase()) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field.text === "") ||
												(this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.NUMERIC &&
													value.value.toString() === cus.field.numeric.toString()) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field.numeric === -1) ||
												(this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.MULTI_SELECT &&
													cus.field.multiLanguageSelectArray
														.map((opt) => opt[currentLanguage].toString().toLowerCase())
														.includes(value.value.toString().toLowerCase())) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field &&
													cus.field.multiLanguageSelectArray &&
													cus.field.multiLanguageSelectArray.map(
														(opt) => opt[currentLanguage]
													).length === 0))
										);
									});

									return custom ? true : false;
								}).length
							: filteredDatas.filter((data) => {
									const custom = data.customFields.find(
										(cus) =>
											cus &&
											cus.field &&
											cus.uid === filter.uid &&
											(((this.getCustomFieldType(computedCustomFields, cus.uid) ===
												TypeCustomFields.TEXT ||
												this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.SELECT) &&
												value.value.toString().toLowerCase() ===
													cus.field.multiLanguageText[currentLanguage]
														.toString()
														.toLowerCase()) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field.multiLanguageText &&
													cus.field.multiLanguageText[currentLanguage] &&
													cus.field.multiLanguageText[currentLanguage] === "") ||
												((this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.URL ||
													this.getCustomFieldType(computedCustomFields, cus.uid) ===
														TypeCustomFields.EMAIL) &&
													value.value.toString().toLowerCase() ===
														cus.field.text.toLowerCase()) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field.text === "") ||
												(this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.NUMERIC &&
													value.value.toString() === cus.field.numeric.toString()) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field.numeric === -1) ||
												(this.getCustomFieldType(computedCustomFields, cus.uid) ===
													TypeCustomFields.MULTI_SELECT &&
													cus.field.multiLanguageSelectArray
														.map((opt) => opt[currentLanguage].toString().toLowerCase())
														.includes(value.value.toString().toLowerCase())) ||
												(value.value.toString().toLowerCase() ===
													this.STranslate.instant("filter.not_specified").toLowerCase() &&
													cus.field &&
													cus.field.multiLanguageSelectArray &&
													cus.field.multiLanguageSelectArray.map(
														(opt) => opt[currentLanguage]
													).length === 0))
									);

									return custom ? true : false;
								}).length;

					value.totalDatas = datasGetType === "firestore" ? total : 1;
					return value;
				});
			} else {
				filter.values = filter.values.map((value) => {
					value.totalDatas = 0;
					return value;
				});
			}
			return filter;
		});

		return { notSelectedDatas: notSelectedDatas, filters: filters };
	}

	/**
	 * getCustomFieldType
	 * @description return type of custom field based on the uid
	 * @param uid
	 * @return number
	 */
	getCustomFieldType(computedCustomFields: IFullCustomField[], uid: string): number {
		return computedCustomFields.find((computedCustomField) => computedCustomField.baseSettings?.uid === uid)
			?.baseSettings?.type;
	}

	generateUuid() {
		return uuidv4();
	}

	detectNavbar(event: IEvent) {
		const isMobile =
			this.platform.is("ios") ||
			this.platform.is("android") ||
			this.platform.is("mobileweb") ||
			window.innerWidth < 768
				? true
				: false;
		return (
			event &&
			event.settings.navigationBarLinks &&
			event.settings.navigationBarLinks.length > 0 &&
			((isMobile && event.settings.activateNavigationBarOnMobile) ||
				(!isMobile && event.settings.activateNavigationBarOnDesktop))
		);
	}
}
